import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Tooltip,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";

import { productDetailsWidgets, reviews } from "../../common/data/ecommerce";

import classnames from "classnames";
// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

import { FreeMode, Navigation } from "swiper/modules";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Swiper } from "swiper/react";
import { SwiperSlide } from "swiper/react";
import { deleteProduct, getProductPlanList, getProductUserSubscriptions, listVariant, viewProduct } from "./_requests";
import "./ecommerce.css";
import DeleteModal from "../../Components/Common/DeleteModal";
import { useToast } from "../../Components/Common/ToastContext";
import { formatDate } from "../../helpers/FunctionHelpers";

interface FormattedData {
  variant: string;
  mrp: string;
  offer_type: number;
  offer_value: number;
  actual_amount: string;
  stock: number;
  sku: string;
}

interface AvailableVariant {
  id: number;
  name: string;
}

interface VariantDetail {
  variant: number;
  value: string;
}

interface VariantData {
  [key: string]: string | number; // Dynamic keys for variant data
}

interface Result {
  variant: VariantDetail[];
  variantData: VariantData;
}
type GroupedVariantData = {
  [key: string]: string[]; // Dynamic variant types with their values
};

const ProductReview = (props: any) => {
  return (
    <React.Fragment>
      <li className="py-2">
        <div className="border border-dashed rounded p-3">
          <div className="d-flex align-items-start mb-3">
            <div className="hstack gap-3">
              <div className="badge rounded-pill bg-success mb-0">
                <i className="mdi mdi-star"></i> {props.review.rating}
              </div>
              <div className="vr"></div>
              <div className="flex-grow-1">
                <p className="text-muted mb-0">{props.review.comment}</p>
              </div>
            </div>
          </div>
          {props.review.subItems && (
            <React.Fragment>
              <div className="d-flex flex-grow-1 gap-2 mb-3">
                {props.review.subItems.map((subItem: any, key: any) => (
                  <React.Fragment key={key}>
                    <Link to="#" className="d-block">
                      <img
                        src={subItem.img}
                        alt=""
                        className="avatar-sm rounded object-fit-cover"
                      />
                    </Link>
                  </React.Fragment>
                ))}
              </div>
            </React.Fragment>
          )}

          <div className="d-flex align-items-end">
            <div className="flex-grow-1">
              <h5 className="fs-15 mb-0">{props.review.name}</h5>
            </div>

            <div className="flex-shrink-0">
              <p className="text-muted mb-0">{props.review.date}</p>
            </div>
          </div>
        </div>
      </li>
    </React.Fragment>
  );
};

const PricingWidgetList = ({ pricingDetails, variantData }: any) => {
  const { mrp, offer_type, offer_value, actual_amount, stock, skuId } =
    variantData || {};

  // Dynamically determine the label details
  const labelDetail =
    pricingDetails?.label === "Price" ? (
      offer_type === 1 || offer_type === 2 ? (
        <div>
          <div>
            <del>MRP: ${mrp}</del>
          </div>{" "}
          {/* MRP on its own line with strikethrough */}
          <div>Price: ${actual_amount}</div>{" "}
          {/* Actual price on the next line */}
          <div className="text-success">
            {offer_type === 1
              ? `${offer_value}% Discount`
              : `Offer Price: $${offer_value}`}
          </div>{" "}
          {/* Discount percentage below the price */}
        </div>
      ) : (
        `MRP: $${actual_amount}`
      ) // Fallback in case no offer_type
    ) : pricingDetails?.label === "Stocks" ? (
      stock
    ) : pricingDetails?.label === "SKU" ? (
      skuId
    ) : null;

  return (
    <React.Fragment>
      <Col lg={6} xl={6}>
        <div className="m-2 p-2 border border-dashed rounded">
          <div className="d-flex align-items-center">
            <div className="avatar-sm me-2">
              <div className="avatar-title rounded bg-transparent text-success fs-24">
                <i className={pricingDetails?.icon}></i>
              </div>
            </div>
            <div className="flex-grow-1">
              <p className="text-muted mb-1">{pricingDetails?.label}</p>
              <h5 className="mb-0">{labelDetail || "N/A"}</h5>
            </div>
          </div>
        </div>
      </Col>
    </React.Fragment>
  );
};

function EcommerceProductDetail(props: any) {
  document.title = " Paal Dappa | Admin panel";
  const { id } = useParams();
  const { showToast } = useToast();
  const history = useNavigate();
  const [editTooltipOpen, setEditTooltipOpen] = useState(false);
  const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");
  const toggleTab = (tab: any) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const [customActiveTab, setcustomActiveTab] = useState<any>("1");
  const toggleCustom = (tab: any) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  const [plans, setPlans] = useState<any[]>([]);
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [subFilter, setSubFilter] = useState("all");
  const [planPage, setPlanPage] = useState(1);
  const [subPage, setSubPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (id) {
      setLoading(true);
      // Fetch both in parallel to show counts immediately
      Promise.all([
        getProductPlanList(id),
        getProductUserSubscriptions(id, subFilter)
      ]).then(([planRes, subRes]) => {
        setPlans(planRes.data || []);
        setUserSubscriptions(subRes.data || []);
        setLoading(false);
      }).catch(err => {
        console.error("Error fetching tab data:", err);
        setLoading(false);
      });
    }
  }, [id, subFilter]); // Refetch if subFilter changes

  const [product, setProduct] = useState<any>(null);
  const [variant, setVariant] = useState<any[]>([]); // Ensure `variant` is initialized as an empty string
  const [variantData, setVariantData] = useState<any[]>([]); // Store the variant combinations
  const [groupedVariantData, setGroupedVariantData] =
    useState<GroupedVariantData>({}); // Store the grouped variant combinations
  const [selectedVariants, setSelectedVariants] = useState<{
    [key: string]: string;
  }>({});

  const [availableVariants, setAvailableVariants] = useState<any[]>([]);
  useEffect(() => {
    viewProduct(id).then((res: any) => {
      setProduct(res.data);
    });
  }, []);


  const sanitizeHTML = useCallback(
    (html: any) => {
      // Replace unwanted tags or attributes here (this is a very basic example).
      return html?.replace(/<script[^>]*>([\S\s]*?)<\/script>/gi, "");
    },
    [product?.longDesc]
  );

  const sanitizedContent = sanitizeHTML(product?.longDesc);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const handleDeleteProduct = async () => {
    if (product) {
      const res: any = await deleteProduct(product.id);
      if (res.status === true) {
        showToast(res.message, res.status);
        history("/products");
      } else {
        showToast(res.message, res.status);
      }
      setDeleteModal(false);
    }
  };

  return (
    <div className="page-content">
      <DeleteModal
        content="Product"
        value={product?.name}
        show={deleteModal}
        onDeleteClick={handleDeleteProduct}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Container fluid>
        <BreadCrumb title="Product Details" pageTitle="Paal Dappa" />

        <Nav tabs className="nav-tabs-custom nav-success mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "overview" })}
              onClick={() => toggleTab("overview")}
              style={{ cursor: "pointer" }}
            >
              Overview
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "plan_list" })}
              onClick={() => toggleTab("plan_list")}
              style={{ cursor: "pointer" }}
            >
              Plan List ({plans.length})
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "user_subscriptions" })}
              onClick={() => toggleTab("user_subscriptions")}
              style={{ cursor: "pointer" }}
            >
              User Subscription List ({userSubscriptions.length})
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="overview">
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <Row className="gx-lg-5">
                      <Col xl={4} md={8} className="mx-auto">
                        <div className="product-img-slider sticky-side-div">
                          <Swiper
                            modules={[Navigation, FreeMode]}
                            navigation={true}
                            centeredSlides={true} // Center active slide
                            slidesPerView={1} // Show one slide at a time
                            spaceBetween={0} // No space between slides
                            className="swiper product-thumbnail-slider p-0 rounded bg-light" // Adjust padding
                          >
                            {product?.galleries?.map((item: any) => (
                              <SwiperSlide
                                key={item.id}
                                className="position-relative"
                              >
                                {item.icon.endsWith(".mp4") ||
                                  item.icon.endsWith(".webm") ? (
                                  <video
                                    className="img-fluid"
                                    style={{
                                      height: "250px",
                                      width: "100%", // Set width to 100% to fit container
                                      objectFit: "cover", // Maintain aspect ratio
                                    }}
                                    controls
                                  >
                                    <source src={item.icon} type="video/mp4" />
                                    <source src={item.icon} type="video/webm" />
                                    Your browser does not support the video tag.
                                  </video>
                                ) : (
                                  <img
                                    src={item.icon}
                                    alt="image"
                                    className="img-fluid"
                                    style={{
                                      height: "250px",
                                      width: "100%", // Set width to 100% to fit container
                                      objectFit: "cover", // Maintain aspect ratio
                                    }}
                                  />
                                )}
                              </SwiperSlide>
                            ))}
                          </Swiper>

                          <div className="product-nav-slider mt-2">
                            <Swiper
                              slidesPerView={4}
                              freeMode={true}
                              watchSlidesProgress={true}
                              spaceBetween={10}
                              className="swiper product-nav-slider mt-2 overflow-hidden"
                            >
                              {product?.galleries?.map((item: any) => (
                                <SwiperSlide key={item.id} className="rounded">
                                  <div className="nav-slide-item">
                                    {item.icon.endsWith(".mp4") ||
                                      item.icon.endsWith(".webm") ? (
                                      <video
                                        className="img-fluid w-100"
                                        style={{
                                          height: "50px",
                                          objectFit: "cover",
                                        }} // Maintain aspect ratio
                                        controls
                                      >
                                        <source src={item.icon} type="video/mp4" />
                                        <source src={item.icon} type="video/webm" />
                                        Your browser does not support the video tag.
                                      </video>
                                    ) : (
                                      <img
                                        src={item.icon}
                                        alt="thumbnail"
                                        className="img-fluid d-block rounded"
                                        style={{
                                          height: "50px",
                                          width: "100%", // Set width to 100% to fit container
                                          objectFit: "cover", // Maintain aspect ratio
                                        }}
                                      />
                                    )}
                                  </div>
                                </SwiperSlide>
                              ))}
                            </Swiper>
                          </div>
                        </div>
                      </Col>

                      <Col xl={8}>
                        <div className="mt-xl-0 mt-5">
                          <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between">
                            <div className="flex-grow-1 mt-3">
                              <h4 >{product?.name}</h4>
                              <h6 className="text-muted mb-0">{product?.billing_name}</h6>
                              <div className="d-flex flex-column flex-sm-row flex-wrap gap-2 gap-sm-3 mt-4">
                                <div className="text-muted">
                                  Brand:{" "}
                                  <span className="text-body fw-medium">
                                    {product?.brands?.name}
                                  </span>
                                </div>
                                <div className="vr d-none d-sm-block"></div>
                                <div className="text-muted">
                                  Category:{" "}
                                  <span className="text-body fw-medium">
                                    {product?.categories?.name}
                                  </span>
                                </div>
                                <div className="vr d-none d-sm-block"></div>
                                <div className="text-muted">
                                  Published:{" "}
                                  <span className="text-body fw-medium">
                                    {formatDate(product?.publishedDate)}
                                  </span>
                                </div>
                                <div className="vr d-none d-sm-block"></div>
                                <div className="text-muted">
                                  Subscription:{" "}
                                  <span className={`badge ${product?.is_subscription_available === true ? 'bg-success' : 'bg-danger'} p-1 px-2`}>
                                    {product?.is_subscription_available === true ? 'Available' : 'Unavailable'}
                                  </span>
                                </div>
                                <div className="vr d-none d-sm-block"></div>
                                <div className="text-muted">
                                  Popular:{" "}
                                  <span className={`badge ${product?.is_popular === 1 ? 'bg-success' : 'bg-danger'} p-1 px-2`}>
                                    {product?.is_popular === 1 ? 'Yes' : 'No'}
                                  </span>
                                </div>
                                <div className="vr d-none d-sm-block"></div>
                                <div className="text-muted">
                                  New Arrival:{" "}
                                  <span className={`badge ${product?.is_new_arrival === 1 ? 'bg-success' : 'bg-danger'} p-1 px-2`}>
                                    {product?.is_new_arrival === 1 ? 'Yes' : 'No'}
                                  </span>
                                </div>
                                <div className="vr d-none d-sm-block"></div>
                                <div className="text-muted">
                                  Listing Order:{" "}
                                  <span className="text-body fw-medium">
                                    {product?.list_order || 0}
                                  </span>
                                </div>
                                <div className="vr d-none d-sm-block"></div>
                                <div className="text-muted">
                                  Average Rating:{" "}
                                  <span className="badge bg-warning text-white p-1 px-2">
                                    <i className="ri-star-fill me-1"></i>
                                    {product?.rating || "0.00"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="d-flex mt-3 mt-md-0 gap-2">
                              <div>
                                <Tooltip
                                  placement="top"
                                  isOpen={editTooltipOpen}
                                  target="EditTooltip"
                                  toggle={() =>
                                    setEditTooltipOpen(!editTooltipOpen)
                                  }
                                >
                                  Edit
                                </Tooltip>
                                <a
                                  href={`/products/edit/${id}`}
                                  id="EditTooltip"
                                  className="btn btn-light"
                                >
                                  <i className="ri-pencil-fill align-bottom"></i>
                                </a>
                              </div>

                              <div>
                                <Tooltip
                                  placement="top"
                                  isOpen={deleteTooltipOpen}
                                  target="DeleteTooltip"
                                  toggle={() =>
                                    setDeleteTooltipOpen(!deleteTooltipOpen)
                                  }
                                >
                                  Delete
                                </Tooltip>
                                <span
                                  onClick={() => setDeleteModal(true)}
                                  id="DeleteTooltip"
                                  className="btn btn-light"
                                >
                                  <i className="mdi mdi-trash-can-outline align-bottom"></i>
                                </span>
                              </div>
                            </div>
                          </div>


                          <Row className="mt-4">
                            <Col xl={12}>
                              <div className="table-responsive">
                                <table className="table table-bordered table-striped">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Variant</th>
                                      <th>MRP</th>
                                      <th>Actual Amount</th>
                                      <th>Offer Type</th>
                                      <th>Offer Value</th>
                                      <th>Routes</th>
                                      <th>Variant Code</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {product?.variants?.map((variant: any) => {
                                      return (
                                        <tr key={variant.sku}>
                                          <td>{variant?.variant_value} {""} {variant.variant?.name}</td>
                                          <td>₹{variant?.mrp}</td>
                                          <td>
                                            ₹{variant?.actual_amount}
                                            <br />
                                            {variant.offer_type != 0 &&
                                              <small className="text-danger">
                                                ({variant?.mrp - variant?.actual_amount > 0 ? `-₹${variant?.mrp - variant?.actual_amount}` : "₹0"})
                                              </small>
                                            }
                                          </td>
                                          <td>
                                            {variant.offer_type == 1
                                              ? "Amount"
                                              : variant.offer_type == 2
                                                ? "Percentage"
                                                : "-"}
                                          </td>
                                          <td>{variant.offer_value}</td>
                                          <td>
                                            <div className="d-flex flex-wrap gap-1">
                                              {variant.route_slots?.length > 0 ? (
                                                variant.route_slots.map((slot: any) => (
                                                  <span key={slot.id} className="badge bg-primary-subtle text-primary border border-primary-subtle p-1 px-2">
                                                    {slot.route?.name || `Route ${slot.route_id}`}
                                                  </span>
                                                ))
                                              ) : (
                                                <span className="text-muted small">No routes</span>
                                              )}
                                            </div>
                                          </td>
                                          <td>{variant.variant_code || "N/A"}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </Col>
                          </Row>



                          {/* 
                      <div className="mt-4 text-muted">
                        <h5 className="fs-15">Description :</h5>
                        <p> {product?.short_description} </p>
                      </div> */}

                          <Row>
                            {product?.features?.length > 0 && (
                              <Col sm={6}>
                                <div className="mt-3">
                                  <h5 className="fs-15">Features :</h5>
                                  <ul className="list-unstyled">
                                    {product.features.map((feature: any, index: any) => (
                                      <li key={index} className="py-1">
                                        <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
                                        {feature?.features?.name}: {feature?.value}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </Col>
                            )}

                            {product?.specifications?.length > 0 && (
                              <Col sm={6}>
                                <div className="mt-3">
                                  <h5 className="fs-15">Specifications :</h5>
                                  <ul className="list-unstyled">
                                    {product.specifications.map((spec: any, index: any) => (
                                      <li key={index} className="py-1">
                                        <i className="mdi mdi-circle-medium me-1 text-muted align-middle"></i>{" "}
                                        {spec?.specifications?.name}: {spec?.value}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </Col>
                            )}
                          </Row>


                          <div className="product-content mt-5">
                            <h5 className="fs-15 mb-3">Product Description :</h5>
                            <Nav tabs className="nav-tabs-custom nav-success">

                              <NavItem>
                                <NavLink
                                  style={{ cursor: "pointer" }}
                                  className={classnames({
                                    active: customActiveTab === "1",
                                  })}
                                  onClick={() => {
                                    toggleCustom("1");
                                  }}
                                >
                                  Specification
                                </NavLink>
                              </NavItem>
                              <NavItem>
                                <NavLink
                                  style={{ cursor: "pointer" }}
                                  className={classnames({
                                    active: customActiveTab === "2",
                                  })}
                                  onClick={() => {
                                    toggleCustom("2");
                                  }}
                                >
                                  Description
                                </NavLink>
                              </NavItem>
                            </Nav>

                            <TabContent
                              activeTab={customActiveTab}
                              className="border border-top-0 p-4"
                              id="nav-tabContent"
                            >
                              <TabPane id="nav-speci" tabId="1">
                                <div className="table-responsive">
                                  <table className="table mb-0">
                                    <tbody>
                                      <tr>
                                        <th scope="row" style={{ width: "200px" }}>
                                          HSN Code
                                        </th>
                                        <td> {product?.hsn_code}</td>
                                      </tr>
                                      <tr>
                                        <th scope="row">GST</th>
                                        <td>
                                          {product?.igst != 0 && product?.igst != null
                                            ? `${product?.igst} (IGST)`
                                            : (product?.cgst == 0 || product?.cgst == null)
                                              ? `0`
                                              : `${product?.cgst} + ${product?.cgst} (CGST + SGST)`}
                                        </td>


                                      </tr>
                                      {/* <tr>
                                    <th scope="row">Weight</th>
                                    <td>{product?.weight}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Length</th>
                                    <td>{product?.length}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Breath</th>
                                    <td>{product?.breath}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Width</th>
                                    <td>{product?.width}</td>
                                  </tr> */}
                                    </tbody>
                                  </table>
                                </div>
                              </TabPane>
                              <TabPane id="nav-detail" tabId="2">
                                <div
                                  className="mt-4"
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizedContent,
                                  }}
                                />
                              </TabPane>
                            </TabContent>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tabId="plan_list">
            <Card>
              <CardBody>
                <h5 className="card-title mb-4">Plans including this product</h5>
                <div className="table-responsive">
                  <table className="table table-bordered table-striped align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>S.No</th>
                        <th>Plan Name</th>
                        <th>Type</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.length > 0 ? (
                        plans.slice((planPage - 1) * pageSize, planPage * pageSize).map((plan: any, index: number) => (
                          <tr key={plan.id}>
                            <td>{(planPage - 1) * pageSize + index + 1}</td>
                            <td className="fw-bold text-info">
                              <Link to={`/plan-subscription/view/${plan.id}`}>{plan.name}</Link>
                            </td>
                            <td>{plan.type === 1 ? "Regular" : plan.type === 2 ? "Combo" : "Trial"}</td>
                            <td>
                              <div className="table-responsive">
                                <table className="table table-sm table-borderless mb-0">
                                  <thead>
                                    <tr className="text-muted">
                                      <th>Variant</th>
                                      <th>Price</th>
                                      <th>Validity</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {plan.details?.map((d: any, idx: number) => (
                                      <tr key={idx}>
                                        <td>{d.variant_value} {d.variant?.name}</td>
                                        <td>₹{d.gross_amount}</td>
                                        <td>{d.validity} {d.validity_type}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center py-4 text-muted">
                            {loading ? "Loading..." : "No plans found for this product."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {plans.length > pageSize && (
                  <div className="d-flex justify-content-end mt-4">
                    <div className="pagination-wrap hstack gap-2">
                       <button className={`page-item pagination-prev ${planPage === 1 ? "disabled" : ""}`} onClick={() => setPlanPage(planPage - 1)}>Previous</button>
                       <ul className="pagination listjs-pagination mb-0">
                         {Array.from({ length: Math.ceil(plans.length / pageSize) }, (_, i) => (
                           <li key={i} className={planPage === i + 1 ? "active" : ""}>
                             <button className="page" onClick={() => setPlanPage(i + 1)}>{i + 1}</button>
                           </li>
                         ))}
                       </ul>
                       <button className={`page-item pagination-next ${planPage === Math.ceil(plans.length / pageSize) ? "disabled" : ""}`} onClick={() => setPlanPage(planPage + 1)}>Next</button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </TabPane>

          <TabPane tabId="user_subscriptions">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h5 className="card-title mb-0">Users subscribed to this product</h5>
                  <div className="d-flex gap-2">
                    <button
                      className={`btn btn-sm ${subFilter === 'all' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setSubFilter('all')}
                    >All</button>
                    <button
                      className={`btn btn-sm ${subFilter === 'current' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setSubFilter('current')}
                    >Current</button>
                    <button
                      className={`btn btn-sm ${subFilter === 'expired' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setSubFilter('expired')}
                    >Expired</button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered table-striped align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>S.No</th>
                        <th>User</th>
                        <th>Plan</th>
                        <th>Duration</th>
                        <th>Deliveries</th>
                        <th>Status</th>
                        <th>Route / Slot</th>
                        <th>Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userSubscriptions.length > 0 ? (
                        userSubscriptions.slice((subPage - 1) * pageSize, subPage * pageSize).map((sub: any, index: number) => (
                          <tr key={sub.id}>
                            <td>{(subPage - 1) * pageSize + index + 1}</td>
                            <td>
                              <div className="fw-bold">{sub.user?.name || "N/A"}</div>
                              <div className="text-muted small">{sub.user?.mobile_number}</div>
                            </td>
                            <td>
                                <div className="text-primary fw-medium">
                                  <Link to={`/view-user-subscription/${sub.id}`}>{sub.subscription?.name}</Link>
                                </div>
                                <div className="text-muted small">
                                    {sub.details?.[0]?.variant_value} {sub.details?.[0]?.variant?.name}
                                </div>
                            </td>
                            <td>
                              <div className="small">Start: {formatDate(sub.start_date)}</div>
                              <div className="small">End: {formatDate(sub.end_date)}</div>
                            </td>
                            <td>
                              <div>Total: {sub.delivery_count}</div>
                              <div className="text-info">Remaining: {sub.remaining_days_plan}</div>
                            </td>
                            <td>
                              <span className={`badge ${sub.is_active ? 'bg-success' : 'bg-danger'}`}>
                                {sub.is_active ? 'Active' : 'Expired'}
                              </span>
                            </td>
                            <td>
                              <div>{sub.route?.name || "N/A"}</div>
                              <span className="badge bg-light text-body border">{sub.slot_type === 1 ? "Morning" : "Evening"}</span>
                            </td>
                            <td style={{ maxWidth: '200px' }}>
                              <div className="small text-truncate" title={sub.addressbook?.address}>
                                {sub.addressbook?.address}
                                {sub.addressbook?.land_mark && <div className="text-muted">({sub.addressbook?.land_mark})</div>}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                             {loading ? "Loading..." : "No user subscriptions found for this product."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {userSubscriptions.length > pageSize && (
                  <div className="d-flex justify-content-end mt-4">
                    <div className="pagination-wrap hstack gap-2">
                       <button className={`page-item pagination-prev ${subPage === 1 ? "disabled" : ""}`} onClick={() => setSubPage(subPage - 1)}>Previous</button>
                       <ul className="pagination listjs-pagination mb-0">
                         {Array.from({ length: Math.ceil(userSubscriptions.length / pageSize) }, (_, i) => (
                           <li key={i} className={subPage === i + 1 ? "active" : ""}>
                             <button className="page" onClick={() => setSubPage(i + 1)}>{i + 1}</button>
                           </li>
                         ))}
                       </ul>
                       <button className={`page-item pagination-next ${subPage === Math.ceil(userSubscriptions.length / pageSize) ? "disabled" : ""}`} onClick={() => setSubPage(subPage + 1)}>Next</button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </TabPane>
        </TabContent>
      </Container>
    </div>
  );
}

export default EcommerceProductDetail;
